import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import type { PropertyCategory } from '../common/property-taxonomy.schema';
import { DEAL_PHASES, type DealPhase } from '../deal/deals.schemas';
import {
  ValuationAgentFailureReasonSchema,
  ValuationAgentRequestStatusSchema,
} from './valuation-agent-request.schemas';

// PRD Resolved Decision 1: „Termin vereinbart“ and every later phase.
export const ACQUISITION_BRIEFING_FIRST_PHASE = 'APPOINTMENT_SCHEDULED' as const satisfies DealPhase;

export function isAcquisitionBriefingPhase(phase: DealPhase): boolean {
  return DEAL_PHASES.indexOf(phase) >= DEAL_PHASES.indexOf(ACQUISITION_BRIEFING_FIRST_PHASE);
}

// Haus, Wohnung, Grundstück: the three profiles the briefing pipeline has (PRD Resolved Decision 7).
export const ACQUISITION_BRIEFING_CATEGORIES = [
  'HOUSE',
  'APARTMENT',
  'LAND',
] as const satisfies ReadonlyArray<PropertyCategory>;

export function isAcquisitionBriefingCategory(category: PropertyCategory): boolean {
  return (ACQUISITION_BRIEFING_CATEGORIES as ReadonlyArray<PropertyCategory>).includes(category);
}

export type BriefingAddress = {
  readonly street: string | null;
  readonly houseNumber: string | null;
  readonly postalCode: string | null;
  readonly city: string | null;
};

export function hasCompleteBriefingAddress(address: BriefingAddress): boolean {
  return [address.street, address.houseNumber, address.postalCode, address.city].every(
    (part) => part !== null && part.trim() !== '',
  );
}

export const AcquisitionBriefingPathParamsSchema = z.object({ organizationId: z.uuid(), id: z.uuid() });

export const StartAcquisitionBriefingBodySchema = z.object({ dealId: refersTo('deal', z.uuid()) }).strict();
export type StartAcquisitionBriefingBody = z.infer<typeof StartAcquisitionBriefingBodySchema>;

export const AcquisitionBriefingRequestSchema = z.object({
  status: ValuationAgentRequestStatusSchema, // effective: a request past its deadline reads FAILED
  reason: ValuationAgentFailureReasonSchema.nullable(), // non-null iff FAILED; TIMED_OUT when derived
  hint: z.string().nullable(), // the agent's note, non-null iff reason === 'ERROR'
  requestedAt: z.iso.datetime(),
});
export type AcquisitionBriefingRequest = z.infer<typeof AcquisitionBriefingRequestSchema>;

export const AcquisitionBriefingStartedResponseSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  latestRequest: AcquisitionBriefingRequestSchema,
});
export type AcquisitionBriefingStartedResponse = z.infer<typeof AcquisitionBriefingStartedResponseSchema>;

export const AcquisitionBriefingStatusResponseSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  latestRequest: AcquisitionBriefingRequestSchema.nullable(), // null: never started
  current: z.object({ deliveredAt: z.iso.datetime(), priceFrom: z.number(), priceTo: z.number() }).nullable(), // null: none delivered yet
});
export type AcquisitionBriefingStatusResponse = z.infer<typeof AcquisitionBriefingStatusResponseSchema>;
